import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { SiteShell } from "@/components/site/SiteShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/site/Logo";
import { supabase } from "@/integrations/supabase/client";
import { signInAdminByPassword } from "@/lib/admin.functions";
import { toast } from "sonner";

export const Route = createFileRoute("/admin-login")({
  head: () => ({
    meta: [
      { title: "Admin Portal — Online Tax VAT Bangladesh" },
      { name: "description", content: "Restricted staff portal for Online Tax VAT Bangladesh administrators to manage orders, deliveries and customer support operations." },
      { name: "robots", content: "noindex, nofollow" },
    ],
    links: [{ rel: "canonical", href: "https://onlinetaxvat.com/admin-login" }],
  }),
  component: AdminLoginPage,
});

function AdminLoginPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await signInAdminByPassword({ data: { password } });
      if (!res.ok) { toast.error(res.error); return; }
      const { error } = await supabase.auth.setSession({
        access_token: res.access_token,
        refresh_token: res.refresh_token,
      });
      if (error) { toast.error("Login failed"); return; }
      try {
        // Lifetime admin session: never ask to log in again.
        localStorage.setItem("otv_remember_until", String(Date.now() + 100 * 365 * 24 * 3600 * 1000));
        sessionStorage.removeItem("otv_session_only");
      } catch {}
      toast.success("Welcome back, Admin!");
      navigate({ to: "/admin" });
    } catch (err: any) {
      toast.error(err?.message ?? "Login failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <SiteShell>
      <section className="pt-14 md:pt-20 pb-20">
        <div className="container-page max-w-md">
          <div className="glass-strong rounded-3xl p-8 shadow-glow">
            <div className="text-center"><Logo className="justify-center" /></div>
            <h1 className="mt-6 font-display font-bold text-2xl text-center">Admin Login</h1>
            <p className="text-sm text-muted-foreground text-center mt-1">শুধুমাত্র admin dashboard access</p>
            <form onSubmit={onSubmit} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="password">Password</Label>
                <div className="relative mt-1.5">
                  <Input id="password" type={showPassword ? "text" : "password"} required value={password}
                    placeholder="আপনার admin password দিন"
                    onChange={(e) => setPassword(e.target.value)} className="pr-10" autoComplete="current-password" />
                  <button type="button" onClick={() => setShowPassword((s) => !s)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full gradient-primary text-primary-foreground shadow-glow">
                {loading ? "Signing in…" : "Sign in as Admin"}
              </Button>
            </form>
            <p className="mt-6 text-xs text-center text-muted-foreground">
              User? <Link to="/login" className="text-primary font-semibold hover:underline">User login</Link>
            </p>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
